import { useSaveAnalysis } from '@modules/analysis/hooks/useSaveAnalysis';
import { useMuiModalContext } from '@modules/common/components/Modal/ModalContext';
import ModalHeader from '@modules/common/components/Modal/ModalHeader';

import { ChooseFeedPreviewTypeForm } from '@modules/common/components/ChooseFeedPreviewTypeForm';
import { SaveFeedAndAddToDashboardFormFooter } from '@modules/common/components/SaveFeedAndAddToDashboardFormFooter';
import {
  useGetArtefactScope,
  useGetDashboardTypeDisplayNameFromURLParam,
  useIsSearchToFeedEnabled,
} from '@modules/common/hooks';
import { DashboardNameAndEmojiInput } from '@modules/dashboard/components/DashboardNameAndEmojiInput';
import { DASHBOARD_NAME_MAX_LENGTH } from '@modules/dashboard/constants';
import { getEmojiFromDashboard } from '@modules/dashboard/helpers';
import {
  DashboardHeader,
  EditableDashboardListItem,
} from '@modules/dashboard/types';
import { useGetFeedDuration, useGetFeedWhereClause } from '@modules/feed/hooks';
import { getSearchMetadataUrl } from '@modules/feed/utils';
import {
  Box,
  DialogContent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { AnalysisUseCase, Scope as EScope } from 'generated/graphql';
import { ChangeEvent, FormEvent, useState } from 'react';
import { Scope } from 'v2/core/entities/scope';
import { EQLProps, ExpressionType } from 'v2/core/repository/eql/types';
import { useGetUser } from 'v2/core/services/user';
import { FeedDefinitionInfo } from '../FeedDefinitionInfo';

interface SaveFeedAndAddToDashboardFormProps {
  onFeedCreate?: () => void;
  dashboardToAddTo?: EditableDashboardListItem;
  setSavedAnalysisRoute?: React.Dispatch<React.SetStateAction<string | null>>;
  shouldAddToNewDashboard?: boolean;
  onClose: () => void;
}

export const SaveFeedAndAddToDashboardForm = ({
  onFeedCreate,
  dashboardToAddTo,
  setSavedAnalysisRoute,
  shouldAddToNewDashboard,
  onClose,
}: SaveFeedAndAddToDashboardFormProps) => {
  const [step, setStep] = useState(0);
  const [feedName, setFeedName] = useState('');
  const [newDashboardName, setNewDashboardName] = useState('');
  const [shouldShowAsFeed, setShouldShowAsFeed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [dialogRefInState, setDialogRefInState] =
    useState<HTMLDivElement | null>(null);
  const [emoji, setEmoji] = useState(getEmojiFromDashboard());

  const duration = useGetFeedDuration();
  const whereClause = useGetFeedWhereClause();
  const isSearchToFeedEnabled = useIsSearchToFeedEnabled();
  const dashboardTypeDisplayName = useGetDashboardTypeDisplayNameFromURLParam(
    dashboardToAddTo?.title,
  );

  const {
    tenant: { slug: tenantSlug },
  } = useGetUser();
  const eqlProps: Omit<EQLProps, 'sourceDetailsMap'> = {
    expressionType: ExpressionType.Records,
    whereClause,
    dateRange: duration as EQLProps['dateRange'],
  };

  const metadata = getSearchMetadataUrl({
    tenantSlug,
    whereClause,
    duration,
    isSearchToFeedEnabled,
  });

  const { onSaveAnalysis } = useSaveAnalysis({
    eqlProps,
    metadata: metadata,
    onAnalysisCreate: onFeedCreate,
    dashboardToAddTo,
    shouldRedirect: false,
    useCase: AnalysisUseCase.Feed,
  });

  const artefactScope = useGetArtefactScope();

  const { closeModal } = useMuiModalContext();

  const isSaveButtonDisabled =
    !feedName.trim() ||
    (shouldAddToNewDashboard &&
      (!newDashboardName?.trim() ||
        newDashboardName.length > DASHBOARD_NAME_MAX_LENGTH));

  const handleCloseModal = () => {
    if (onClose) {
      onClose();
      return;
    }
    closeModal();
  };

  const onNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setFeedName(value);
  };

  const onSaveFeed = async (
    name: string,
    scope: Scope,
    newDashboard?: DashboardHeader,
  ) => {
    const artefactDetails = await onSaveAnalysis({
      name,
      scope,
      newDashboard,
      shouldShowAsFeed,
    });
    setSavedAnalysisRoute?.(artefactDetails.artefactRoute);
    return artefactDetails;
  };

  const onSaveFeedAnalysis = async (e: FormEvent) => {
    e.preventDefault();

    const analysisScope =
      artefactScope === EScope.Private ? Scope.PRIVATE : Scope.PUBLIC;

    if (isSaveButtonDisabled) {
      return;
    }

    setIsLoading(true);
    await onSaveFeed(feedName, analysisScope, {
      name: newDashboardName ?? '',
      emoji: emoji,
    });
    handleCloseModal();
  };

  const title = 'Save and Select Dashboard view';

  const renderFeedDetailsForm = () => {
    return (
      <Stack py={2} gap={4}>
        {dashboardToAddTo && (
          <Typography variant="subtitle2" color="text.secondary">
            Save this Feed to add to {dashboardTypeDisplayName}
          </Typography>
        )}
        {shouldAddToNewDashboard && (
          <DashboardNameAndEmojiInput
            emoji={emoji}
            onEmojiChange={setEmoji}
            name={newDashboardName}
            onNameChange={setNewDashboardName}
            dialogRef={dialogRefInState}
          />
        )}
        <TextField
          autoFocus={!shouldAddToNewDashboard}
          label="Feed Name"
          type={'text'}
          placeholder={'Enter descriptive name'}
          name={`feed-name`}
          value={feedName}
          onChange={onNameChange}
          autoComplete="off"
        />

        <Box
          sx={{
            borderRadius: 1,
            backgroundColor: 'background.default',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box p={2} color="text.secondary">
            <FeedDefinitionInfo />
          </Box>
        </Box>
      </Stack>
    );
  };

  return (
    <>
      <ModalHeader title={title} showCloseButton={false} />
      <Box
        component="form"
        onSubmit={onSaveFeedAnalysis}
        ref={setDialogRefInState}
      >
        <DialogContent dividers>
          {step === 0 ? (
            renderFeedDetailsForm()
          ) : (
            <ChooseFeedPreviewTypeForm
              setShouldShowAsFeed={setShouldShowAsFeed}
              shouldShowAsFeed={shouldShowAsFeed}
              containrSx={{ minHeight: '315px' }}
            />
          )}
        </DialogContent>
        <SaveFeedAndAddToDashboardFormFooter
          step={step}
          isDashboardExists={true}
          isLoading={isLoading}
          isSaveButtonDisabled={isSaveButtonDisabled}
          newDashboardName={newDashboardName}
          shouldAddToNewDashboard={shouldAddToNewDashboard}
          onCancel={handleCloseModal}
          onBack={() => setStep(0)}
          onNext={() => setStep(1)}
        />
      </Box>
    </>
  );
};
